import React, { useState, useEffect } from 'react';
import { stockTransferAPI, propertyAPI, storeAPI, itemAPI, unitAPI, stockAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import { SearchableItemSelect } from '../components/SearchableItemSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, ArrowLeftRight, Loader2, Trash2, Eye, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatNumber } from '../lib/utils';

export const StockTransferPage = () => {
  const [records, setRecords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({
    property_id: '',
    from_store_id: '',
    to_store_id: '',
    items: [{ item_id: '', quantity: '', unit_id: '' }],
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, propertiesRes, storesRes, itemsRes, unitsRes, stockRes] = await Promise.all([
        stockTransferAPI.getAll(),
        propertyAPI.getAll({ status: 'active' }),
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        stockAPI.getAll()
      ]);
      setRecords(recordsRes.data);
      setProperties(propertiesRes.data);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setStock(stockRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setFormData({
      property_id: properties[0]?.id || '',
      from_store_id: '',
      to_store_id: '',
      items: [{ item_id: '', quantity: '', unit_id: '' }],
      notes: ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { item_id: '', quantity: '', unit_id: '' }]
    });
  };

  const handleRemoveItem = (index) => {
    if (formData.items.length > 1) {
      setFormData({
        ...formData,
        items: formData.items.filter((_, i) => i !== index)
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'item_id') {
      const selectedItem = items.find(i => i.id === value);
      if (selectedItem?.default_unit_id) {
        newItems[index].unit_id = selectedItem.default_unit_id;
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const getAvailableStock = (itemId, storeId) => {
    const stockRecord = stock.find(s => s.item_id === itemId && s.store_id === storeId);
    return stockRecord?.quantity || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.from_store_id === formData.to_store_id) {
      toast.error('From and To stores cannot be the same');
      return;
    }

    const validItems = formData.items.filter(item => item.item_id && item.quantity);
    if (validItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        items: validItems.map(item => ({
          item_id: item.item_id,
          quantity: parseFloat(item.quantity) || 0,
          unit_id: item.unit_id
        }))
      };
      
      await stockTransferAPI.create(data);
      toast.success('Stock transfer recorded successfully');
      handleCloseDialog();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save record');
    } finally {
      setSaving(false);
    }
  };

  const filteredStores = formData.property_id
    ? stores.filter(s => s.property_id === formData.property_id)
    : stores;

  const toStoreOptions = filteredStores.filter(s => s.id !== formData.from_store_id);

  // Status badge colors
  const getStatusBadge = (status) => {
    const statusConfig = {
      'in_transit': { label: 'In Transit', className: 'bg-blue-100 text-blue-700 border-blue-300' },
      'pending_acknowledgement': { label: 'Pending', className: 'bg-amber-100 text-amber-700 border-amber-300' },
      'accepted': { label: 'Accepted', className: 'bg-green-100 text-green-700 border-green-300' },
      'rejected': { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-300' },
      'partially_accepted': { label: 'Partial', className: 'bg-orange-100 text-orange-700 border-orange-300' },
      'disputed': { label: 'Disputed', className: 'bg-purple-100 text-purple-700 border-purple-300' }
    };
    const config = statusConfig[status] || { label: status || 'Pending', className: 'bg-amber-100 text-amber-700' };
    return <Badge variant="outline" className={`${config.className} text-xs`}>{config.label}</Badge>;
  };

  const columns = [
    { key: 'transaction_number', label: 'Transaction #', render: (val) => <span className="font-medium font-mono">{val}</span> },
    { key: 'date', label: 'Date', render: (val) => formatDate(val) },
    { key: 'property_name', label: 'Property', render: (val) => val || '-' },
    { 
      key: 'from_store_name', 
      label: 'From → To', 
      render: (val, row) => (
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">{val}</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">{row.to_store_name}</span>
        </div>
      )
    },
    { key: 'items', label: 'Items', render: (val) => `${val?.length || 0} items` },
    { 
      key: 'status', 
      label: 'Status', 
      render: (val) => getStatusBadge(val)
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Stock Transfer</h1>
          <p className="text-muted-foreground mt-1">Transfer stock between stores</p>
        </div>
        <Button onClick={handleOpenDialog} className="rounded-full" data-testid="add-stock-transfer-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Transfer Entry
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable
            module="stock_transfer"
            data={records}
            loading={loading}
            title="Stock Transfers"
            columns={columns}
            renderActions={(record) => (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setSelectedRecord(record);
                  setViewDialogOpen(true);
                }}
                data-testid={`view-transfer-${record.id}`}
              >
                <Eye className="h-4 w-4" />
              </Button>
            )}
          />
        </CardContent>
      </Card>

      {/* New Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[1000px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Stock Transfer Entry</DialogTitle>
            <DialogDescription>
              Transfer stock from one store to another
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Property *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => setFormData({ ...formData, property_id: value, from_store_id: '', to_store_id: '' })}
                >
                  <SelectTrigger data-testid="transfer-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Store *</Label>
                  <Select
                    value={formData.from_store_id}
                    onValueChange={(value) => setFormData({ ...formData, from_store_id: value })}
                  >
                    <SelectTrigger data-testid="transfer-from-store-select">
                      <SelectValue placeholder="Select source store" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredStores.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>To Store *</Label>
                  <Select
                    value={formData.to_store_id}
                    onValueChange={(value) => setFormData({ ...formData, to_store_id: value })}
                  >
                    <SelectTrigger data-testid="transfer-to-store-select">
                      <SelectValue placeholder="Select destination store" />
                    </SelectTrigger>
                    <SelectContent>
                      {toStoreOptions.map((s) => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Items Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Items *</Label>
                  <Button type="button" variant="outline" size="sm" onClick={handleAddItem} data-testid="add-transfer-item-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[35%]">Item</TableHead>
                        <TableHead className="w-[20%]">Available</TableHead>
                        <TableHead className="w-[15%]">Quantity</TableHead>
                        <TableHead className="w-[20%]">Unit</TableHead>
                        <TableHead className="w-[10%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => {
                        const availableQty = getAvailableStock(item.item_id, formData.from_store_id);
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <SearchableItemSelect
                                items={items}
                                value={item.item_id}
                                onSelect={(value) => handleItemChange(index, 'item_id', value)}
                                placeholder="Search & select item..."
                                testId={`transfer-item-${index}-select`}
                              />
                            </TableCell>
                            <TableCell>
                              <span className={`font-medium ${availableQty <= 0 ? 'text-destructive' : 'text-green-600'}`}>
                                {formatNumber(availableQty)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                max={availableQty}
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                data-testid={`transfer-qty-${index}-input`}
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                value={item.unit_id || "none"}
                                onValueChange={(value) => handleItemChange(index, 'unit_id', value === "none" ? "" : value)}
                              >
                                <SelectTrigger data-testid={`transfer-unit-${index}-select`}>
                                  <SelectValue placeholder="Unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Select unit</SelectItem>
                                  {units.map((u) => (
                                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell>
                              {formData.items.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleRemoveItem(index)}
                                  data-testid={`remove-transfer-item-${index}`}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="transfer-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-transfer-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px]">
          <DialogHeader>
            <DialogTitle>Stock Transfer Details</DialogTitle>
            <DialogDescription>
              Transaction: {selectedRecord?.transaction_number}
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Date</p>
                  <p className="font-medium">{formatDate(selectedRecord.date)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Property</p>
                  <p className="font-medium">{selectedRecord.property_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">From Store</p>
                  <p className="font-medium">{selectedRecord.from_store_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">To Store</p>
                  <p className="font-medium">{selectedRecord.to_store_name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Initiated By</p>
                  <p className="font-medium">{selectedRecord.initiated_by_name}</p>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRecord.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.quantity)}</TableCell>
                        <TableCell>{item.unit_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {selectedRecord.notes && (
                <div>
                  <p className="text-muted-foreground text-sm">Notes</p>
                  <p className="text-sm">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockTransferPage;
